import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
};

export default function Routes({ user }) {
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [zones, setZones] = useState([]);
    const [clients, setClients] = useState([]);
    const [showImportScreen, setShowImportScreen] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [trips, setTrips] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [tripModal, setTripModal] = useState(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchZones();
        fetchClients();
        fetchSubscription();
    }, []);

    const fetchSubscription = async () => {
        try {
            const res = await axios.get(`${API}/subscriptions/operator/${operatorId}`);
            if (Array.isArray(res.data)) {
                const active = res.data.find(s => s.status === 'Active');
                setSubscription(active || null);
            }
        } catch (err) { console.error(err); }
    };

    const fetchClients = async () => {
        try {
            const res = await axios.get(`${API}/clients/${operatorId}`);
            setClients(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchZones = async () => {
        try {
            const res = await axios.get(`${API}/master-data`);
            setZones(res.data.filter(z => z.category === 'route-zone'));
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [routesRes, tripsRes, allocRes] = await Promise.all([
                axios.get(`${API}/routes/${operatorId}`),
                axios.get(`${API}/trips/${operatorId}`),
                axios.get(`${API}/passenger-allocations/${operatorId}`)
            ]);
            setRoutes(routesRes.data);
            setTrips(tripsRes.data);
            setAllocations(allocRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleExport = () => {
        if (routes.length === 0) { alert('No routes to export.'); return; }
        const exportData = routes.map(r => ({
            'Route Name': r.name || '',
            'Pickup Location': r.pickupLocation || '',
            'Drop Location': r.dropLocation || '',
            'Distance (km)': r.distance || '',
            'Estimated Minutes': r.estimatedMinutes || '',
            'Client': r.clientId?.companyName || '',
            'Zone': r.zone?.name || '',
            'Description': r.description || '',
            'Status': r.status || 'Active',
            'Created At': r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : ''
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 22 }, { wch: 35 }, { wch: 35 },
            { wch: 14 }, { wch: 18 }, { wch: 20 },
            { wch: 14 }, { wch: 30 }, { wch: 10 }, { wch: 14 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Routes');
        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Routes_Export_${timestamp}.xlsx`);
    };

    const handleSave = async (formData, formStops = []) => {
        try {
            const payload = { ...formData, operatorId };
            let routeId = modal.data?._id;
            if (routeId) {
                await axios.put(`${API}/routes/${routeId}`, payload);
            } else {
                const res = await axios.post(`${API}/routes`, payload);
                routeId = res.data.route._id;
            }

            if (formStops.length > 0 || modal.data?._id) {
                const existingRes = await axios.get(`${API}/route-stops/${routeId}`);
                const existingStops = existingRes.data;
                for (const exStop of existingStops) {
                    if (!formStops.some(s => s._id === exStop._id)) {
                        await axios.delete(`${API}/route-stops/${exStop._id}`);
                    }
                }
                for (let i = 0; i < formStops.length; i++) {
                    const s = formStops[i];
                    const stopPayload = { ...s, order: i, routeId, operatorId };
                    if (s._id) {
                        await axios.put(`${API}/route-stops/${s._id}`, stopPayload);
                    } else {
                        await axios.post(`${API}/route-stops`, stopPayload);
                    }
                }
            }

            setModal(null);
            fetchData();
        } catch (err) { 
            console.error(err);
            alert(err.response?.data?.message || "Failed to save route. Please try again.");
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/routes/${deleteModal._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const filtered = routes.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.pickupLocation.toLowerCase().includes(search.toLowerCase()) ||
        r.dropLocation.toLowerCase().includes(search.toLowerCase())
    );

    // Show the Import Wizard screen
    if (showImportScreen) {
        return (
            <ImportWizard
                routes={routes}
                operatorId={operatorId}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchData(); }}
            />
        );
    }

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Routes Management</h1>
                    <p className="md-subtitle">Create and manage transport routes for your operations.</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {(() => {
                        const limit = subscription?.planId?.routesLimit;
                        const isUnlimited = !limit || limit === -1 || limit === '-1' || limit === 'Unlimited';
                        const atLimit = !isUnlimited && routes.length >= Number(limit);
                        return atLimit ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, background: '#fef2f2', border: '1.5px solid #fca5a5', color: '#dc2626', fontSize: 13, fontWeight: 700 }}>
                                ⚠️ Route limit reached ({routes.length}/{limit}) — Please upgrade your plan
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={() => setShowImportScreen(true)}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, border: '1.5px solid #c3dcf5', background: '#eef2ff', color: '#11427D', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}
                                >
                                    <Icon d={icons.upload} size={16} strokeWidth={2.5} />
                                    Import
                                </button>
                                <button
                                    onClick={() => setModal({ data: null })}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}
                                >
                                    <Icon d={icons.plus} size={16} strokeWidth={2.5} />
                                    Add Route
                                </button>
                            </>
                        );
                    })()}
                    <button
                        onClick={handleExport}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, border: '1.5px solid #a7f3d0', background: '#ecfdf5', color: '#065f46', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <Icon d={icons.download} size={16} strokeWidth={2.5} />
                        Export
                    </button>
                </div>
            </div>

            <div className="md-panel">
                <div className="md-panel-toolbar">
                    <div className="md-search-wrap">
                        <Icon d={icons.search} size={14} />
                        <input
                            className="md-input md-search"
                            placeholder="Search routes..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading routes...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Route Name</th>
                                    <th className="md-th">Pickup Location</th>
                                    <th className="md-th">Drop Location</th>
                                    <th className="md-th">Distance</th>
                                    <th className="md-th">Trips</th>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Zone</th>
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(r => {
                                    const routeTrips = trips.filter(t => (t.routeId?._id || t.routeId) === r._id);
                                    return (
                                    <tr key={r._id}>
                                        <td className="md-td"><strong>{r.name}</strong></td>
                                        <td className="md-td">{r.pickupLocation}</td>
                                        <td className="md-td">{r.dropLocation}</td>
                                        <td className="md-td">{r.distance || 0} km</td>
                                        <td className="md-td">
                                            {routeTrips.length > 0 ? (
                                                <button 
                                                    onClick={() => setTripModal({ route: r, trips: routeTrips })}
                                                    style={{ background: '#e6f0fa', color: '#11427D', border: 'none', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s' }}
                                                    onMouseOver={e => e.currentTarget.style.background = '#c3dcf5'}
                                                    onMouseOut={e => e.currentTarget.style.background = '#e6f0fa'}
                                                >
                                                    {routeTrips.length} {routeTrips.length === 1 ? 'Trip' : 'Trips'}
                                                </button>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>0 Trips</span>
                                            )}
                                        </td>
                                        <td className="md-td">{r.clientId?.companyName || '—'}</td>
                                        <td className="md-td">{r.zone?.name || '—'}</td>
                                        <td className="md-td">
                                            <span className={`md-status-badge ${r.status === 'Active' ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="md-td">
                                            <div className="md-actions">
                                                <button className="md-icon-btn" onClick={() => setModal({ data: r })}><Icon d={icons.edit} size={14} /></button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(r)}><Icon d={icons.trash} size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {modal && (
                <RouteForm
                    data={modal.data}
                    zones={zones}
                    clients={clients}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}

            {/* Delete Confirmation */}
            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Delete</h3>
                        </div>
                        <div className="md-modal-body">
                            Are you sure you want to delete route "<strong>{deleteModal.name}</strong>"? This action cannot be undone.
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Trip Details Modal */}
            {tripModal && (
                <div className="md-modal-overlay" onClick={() => setTripModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title">Trips for {tripModal.route.name}</h3>
                            <button className="md-modal-close" onClick={() => setTripModal(null)}><Icon d={icons.x} size={18} /></button>
                        </div>
                        <div className="md-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto', padding: 0 }}>
                            <table className="md-table" style={{ margin: 0, borderTop: 'none' }}>
                                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                    <tr>
                                        <th className="md-th">Trip Name</th>
                                        <th className="md-th">Date</th>
                                        <th className="md-th">Passengers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tripModal.trips.map(t => {
                                        const passCount = allocations.filter(a => (a.tripId?._id || a.tripId) === t._id).length;
                                        const dateStr = t.tripFrequency === 'Recurring' 
                                            ? (t.startDate ? new Date(t.startDate).toLocaleDateString() : 'Recurring')
                                            : (t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—');
                                        return (
                                            <tr key={t._id}>
                                                <td className="md-td"><strong>{t.tripName || (t.tripType === 'on_demand' ? 'Adhoc Trip' : '—')}</strong></td>
                                                <td className="md-td">{dateStr}</td>
                                                <td className="md-td">
                                                    <span style={{ background: '#f0fdf4', color: '#166534', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '800' }}>
                                                        {passCount} {passCount === 1 ? 'Passenger' : 'Passengers'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function RouteForm({ data, zones, clients, onClose, onSave }) {
    const [form, setForm] = useState(data ? {
        ...data,
        zone: data.zone?._id || data.zone || '',
        clientId: data.clientId?._id || data.clientId || ''
    } : {
        name: '',
        pickupLocation: '',
        pickupLat: null,
        pickupLng: null,
        dropLocation: '',
        dropLat: null,
        dropLng: null,
        distance: '',
        estimatedMinutes: '',
        zone: '',
        clientId: '',
        description: '',
        status: 'Active'
    });

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const routeLayer = useRef(null);
    const pickupMarker = useRef(null);
    const dropMarker = useRef(null);

    const [pickupQuery, setPickupQuery] = useState('');
    const [dropQuery, setDropQuery] = useState('');
    const [pickupSuggestions, setPickupSuggestions] = useState([]);
    const [dropSuggestions, setDropSuggestions] = useState([]);
    const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
    const [showDropSuggestions, setShowDropSuggestions] = useState(false);

    const [stops, setStops] = useState([]);
    const [stopQuery, setStopQuery] = useState('');
    const [stopSuggestions, setStopSuggestions] = useState([]);
    const [showStopSuggestions, setShowStopSuggestions] = useState(false);
    const stopMarkers = useRef([]);
    const formRef = useRef(form);
    const stopsRef = useRef(stops);
    useEffect(() => { formRef.current = form; }, [form]);
    useEffect(() => { stopsRef.current = stops; }, [stops]);

    // Initialize Map
    useEffect(() => {
        if (!mapInstance.current && mapRef.current) {
            mapInstance.current = L.map(mapRef.current).setView([20.5937, 78.9629], 5); // India center
            L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            }).addTo(mapInstance.current);

            // Default marker icons configuration for Leaflet
            delete L.Icon.Default.prototype._getIconUrl;
            L.Icon.Default.mergeOptions({
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
            });

            // Map click listener
            mapInstance.current.on('click', async (e) => {
                const lat = e.latlng.lat;
                const lng = e.latlng.lng;
                
                let name = `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
                try {
                    const res = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
                    if (res.data && res.data.display_name) {
                        name = res.data.display_name;
                    }
                } catch (err) { console.error("Reverse geocode failed", err); }

                const currentForm = formRef.current;
                if (!currentForm.pickupLat) {
                    setForm(prev => ({ ...prev, pickupLocation: name, pickupLat: lat, pickupLng: lng }));
                    setPickupQuery(name);
                    if (pickupMarker.current) mapInstance.current.removeLayer(pickupMarker.current);
                    pickupMarker.current = L.marker([lat, lng], { title: 'Pickup' }).addTo(mapInstance.current);
                } else if (!currentForm.dropLat) {
                    setForm(prev => ({ ...prev, dropLocation: name, dropLat: lat, dropLng: lng }));
                    setDropQuery(name);
                    if (dropMarker.current) mapInstance.current.removeLayer(dropMarker.current);
                    dropMarker.current = L.marker([lat, lng], { title: 'Drop' }).addTo(mapInstance.current);
                } else {
                    setStops(prev => [...prev, { name, lat, lng, order: prev.length }]);
                }
            });
        }
    }, []);

    useEffect(() => {
        if (data && data._id) {
            axios.get(`${API}/route-stops/${data._id}`).then(res => {
                setStops(res.data.sort((a,b) => a.order - b.order));
            }).catch(err => console.error(err));
        }
    }, [data]);

    useEffect(() => {
        stopMarkers.current.forEach(m => mapInstance.current?.removeLayer(m));
        stopMarkers.current = [];
        if (!mapInstance.current) return;
        stops.forEach((stop, i) => {
            const m = L.circleMarker([stop.lat, stop.lng], {
                radius: 6, fillColor: '#16539C', color: '#fff', weight: 2, fillOpacity: 1
            }).bindTooltip(`${i+1}. ${stop.name}`).addTo(mapInstance.current);
            stopMarkers.current.push(m);
        });
    }, [stops]);

    // Nominatim Search
    const searchLocation = async (query) => {
        if (!query || query.length < 3) return [];
        try {
            const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`);
            return res.data;
        } catch (error) {
            console.error("Nominatim search error:", error);
            return [];
        }
    };

    // Debounced search for Pickup
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (pickupQuery && pickupQuery !== form.pickupLocation) {
                const results = await searchLocation(pickupQuery);
                setPickupSuggestions(results);
                setShowPickupSuggestions(true);
            } else {
                setPickupSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [pickupQuery, form.pickupLocation]);

    // Debounced search for Drop
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (dropQuery && dropQuery !== form.dropLocation) {
                const results = await searchLocation(dropQuery);
                setDropSuggestions(results);
                setShowDropSuggestions(true);
            } else {
                setDropSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [dropQuery, form.dropLocation]);

    // Debounced search for Stop
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (stopQuery) {
                const results = await searchLocation(stopQuery);
                setStopSuggestions(results);
                setShowStopSuggestions(true);
            } else {
                setStopSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [stopQuery]);

    const handleSelectLocation = (place, type) => {
        const lat = parseFloat(place.lat);
        const lon = parseFloat(place.lon);
        const displayName = place.display_name;

        if (type === 'pickup') {
            setForm(prev => ({ ...prev, pickupLocation: displayName, pickupLat: lat, pickupLng: lon }));
            setPickupQuery(displayName);
            setShowPickupSuggestions(false);
            if (pickupMarker.current) mapInstance.current.removeLayer(pickupMarker.current);
            pickupMarker.current = L.marker([lat, lon], { title: 'Pickup' }).addTo(mapInstance.current);
        } else {
            setForm(prev => ({ ...prev, dropLocation: displayName, dropLat: lat, dropLng: lon }));
            setDropQuery(displayName);
            setShowDropSuggestions(false);
            if (dropMarker.current) mapInstance.current.removeLayer(dropMarker.current);
            dropMarker.current = L.marker([lat, lon], { title: 'Drop' }).addTo(mapInstance.current);
        }

        // Center map
        if (mapInstance.current) {
            mapInstance.current.setView([lat, lon], 12);
        }
    };

    // Calculate route when locations change (OSRM)
    useEffect(() => {
        const calculateRoute = async () => {
            if (!mapInstance.current || !form.pickupLat || !form.pickupLng || !form.dropLat || !form.dropLng) return;

            try {
                // OSRM expects coordinates in lon,lat format
                const coords = [];
                coords.push(`${form.pickupLng},${form.pickupLat}`);
                stops.forEach(s => {
                    if (s.lng && s.lat) coords.push(`${s.lng},${s.lat}`);
                });
                coords.push(`${form.dropLng},${form.dropLat}`);

                const url = `https://router.project-osrm.org/route/v1/driving/${coords.join(';')}?overview=full&geometries=geojson`;
                const response = await axios.get(url);
                
                if (response.data && response.data.routes && response.data.routes.length > 0) {
                    const route = response.data.routes[0];
                    const distKm = (route.distance / 1000).toFixed(2);
                    const durationMin = Math.round(route.duration / 60);
                    
                    setForm(prev => ({ ...prev, distance: distKm, estimatedMinutes: durationMin }));

                    // Draw route on map
                    if (routeLayer.current) {
                        mapInstance.current.removeLayer(routeLayer.current);
                    }
                    
                    const geojsonRoute = {
                        type: "Feature",
                        properties: {},
                        geometry: route.geometry
                    };
                    
                    routeLayer.current = L.geoJSON(geojsonRoute, {
                        style: {
                            color: '#16539C',
                            weight: 5,
                            opacity: 0.7
                        }
                    }).addTo(mapInstance.current);
                    
                    // Fit bounds to route
                    mapInstance.current.fitBounds(routeLayer.current.getBounds(), { padding: [50, 50] });
                }
            } catch (error) {
                console.error("OSRM routing error:", error);
            }
        };

        const timer = setTimeout(calculateRoute, 800); 
        return () => clearTimeout(timer);
    }, [form.pickupLat, form.pickupLng, form.dropLat, form.dropLng, stops]);

    // Close suggestions on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.pickup-search-container')) setShowPickupSuggestions(false);
            if (!e.target.closest('.drop-search-container')) setShowDropSuggestions(false);
            if (!e.target.closest('.stop-search-container')) setShowStopSuggestions(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Set initial query values if editing
    useEffect(() => {
        if (data && data.pickupLocation) setPickupQuery(data.pickupLocation);
        if (data && data.dropLocation) setDropQuery(data.dropLocation);
    }, [data]);

    // Autofill Zone based on Distance
    useEffect(() => {
        if (form.distance && !isNaN(parseFloat(form.distance)) && zones && zones.length > 0) {
            const dist = parseFloat(form.distance);
            const covering = zones.filter(z => (z.distance || 0) >= dist);
            if (covering.length > 0) {
                const minCoverage = Math.min(...covering.map(z => z.distance || 0));
                const bestZones = covering.filter(z => (z.distance || 0) === minCoverage);
                if (bestZones.length > 0 && form.zone !== bestZones[0]._id) {
                    setForm(prev => ({ ...prev, zone: bestZones[0]._id }));
                }
            }
        }
    }, [form.distance, zones]);

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" style={{ maxWidth: 1100, width: '95%' }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">{data ? 'Edit Route' : 'Add New Route'}</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <div className="md-modal-body" style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '24px', padding: '24px' }}>
                    <div className="form-fields" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div className="md-form-group">
                            <label className="md-label">Route Name*</label>
                            <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. IT Park Shuttle" />
                        </div>

                        <div className="md-form-group pickup-search-container" style={{ position: 'relative' }}>
                            <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                Pickup Location*
                                {form.pickupLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className="md-input"
                                    value={pickupQuery}
                                    onChange={e => {
                                        setPickupQuery(e.target.value);
                                        // Clear lat/lng if user types manually to force re-selection
                                        if (form.pickupLat) {
                                            setForm({ ...form, pickupLocation: e.target.value, pickupLat: null, pickupLng: null });
                                        }
                                    }}
                                    onFocus={() => { if (pickupSuggestions.length > 0) setShowPickupSuggestions(true); }}
                                    placeholder="Search for pickup point..."
                                    style={{ paddingLeft: '32px' }}
                                />
                                <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                    <Icon d={icons.search} size={14} />
                                </div>
                            </div>
                            {showPickupSuggestions && pickupSuggestions.length > 0 && (
                                <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                                    {pickupSuggestions.map(place => (
                                        <li key={place.place_id} onClick={() => handleSelectLocation(place, 'pickup')} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                            {place.display_name}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="md-form-group drop-search-container" style={{ position: 'relative' }}>
                            <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                Drop Location*
                                {form.dropLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className="md-input"
                                    value={dropQuery}
                                    onChange={e => {
                                        setDropQuery(e.target.value);
                                        if (form.dropLat) {
                                            setForm({ ...form, dropLocation: e.target.value, dropLat: null, dropLng: null });
                                        }
                                    }}
                                    onFocus={() => { if (dropSuggestions.length > 0) setShowDropSuggestions(true); }}
                                    placeholder="Search for destination..."
                                    style={{ paddingLeft: '32px' }}
                                />
                                <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                    <Icon d={icons.search} size={14} />
                                </div>
                            </div>
                            {showDropSuggestions && dropSuggestions.length > 0 && (
                                <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                                    {dropSuggestions.map(place => (
                                        <li key={place.place_id} onClick={() => handleSelectLocation(place, 'drop')} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                            {place.display_name}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {form.pickupLat && form.dropLat && (
                            <div className="md-form-group stop-search-container" style={{ position: 'relative', marginTop: 10, background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    Intermediate Stops ({stops.length})
                                </label>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                    {stops.map((stop, i) => (
                                        <div key={stop._id || i} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e6f0fa', color: '#11427D', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                                                {i + 1}
                                            </div>
                                            <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{stop.name}</div>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button 
                                                    className="md-icon-btn" 
                                                    onClick={() => {
                                                        const newStops = [...stops];
                                                        if (i > 0) {
                                                            [newStops[i-1], newStops[i]] = [newStops[i], newStops[i-1]];
                                                            setStops(newStops);
                                                        }
                                                    }}
                                                    disabled={i === 0}
                                                >
                                                    <Icon d={icons.up || ['M18 15l-6-6-6 6']} size={14} />
                                                </button>
                                                <button 
                                                    className="md-icon-btn" 
                                                    onClick={() => {
                                                        const newStops = [...stops];
                                                        if (i < stops.length - 1) {
                                                            [newStops[i+1], newStops[i]] = [newStops[i], newStops[i+1]];
                                                            setStops(newStops);
                                                        }
                                                    }}
                                                    disabled={i === stops.length - 1}
                                                >
                                                    <Icon d={icons.down || ['M6 9l6 6 6-6']} size={14} />
                                                </button>
                                                <button 
                                                    className="md-icon-btn md-icon-btn-danger" 
                                                    onClick={() => setStops(stops.filter((_, idx) => idx !== i))}
                                                >
                                                    <Icon d={icons.trash} size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="md-input"
                                        value={stopQuery}
                                        onChange={e => setStopQuery(e.target.value)}
                                        onFocus={() => { if (stopSuggestions.length > 0) setShowStopSuggestions(true); }}
                                        placeholder="Add an intermediate stop..."
                                        style={{ paddingLeft: '32px' }}
                                    />
                                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                        <Icon d={icons.search} size={14} />
                                    </div>
                                </div>
                                {showStopSuggestions && stopSuggestions.length > 0 && (
                                    <ul style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                                        {stopSuggestions.map(place => (
                                            <li key={place.place_id} onClick={() => {
                                                const lat = parseFloat(place.lat);
                                                const lon = parseFloat(place.lon);
                                                setStops([...stops, { name: place.display_name, lat, lng: lon, order: stops.length }]);
                                                setStopQuery('');
                                                setShowStopSuggestions(false);
                                            }} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                                {place.display_name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="md-form-group">
                                <label className="md-label">Distance (km)</label>
                                <input
                                    type="number"
                                    className="md-input"
                                    value={form.distance}
                                    onChange={e => setForm({ ...form, distance: e.target.value })}
                                    placeholder="0.00"
                                    step="0.01"
                                />
                            </div>
                            <div className="md-form-group">
                                <label className="md-label">Est. Minutes</label>
                                <input
                                    type="number"
                                    className="md-input"
                                    value={form.estimatedMinutes}
                                    onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })}
                                    placeholder="Minutes"
                                />
                            </div>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Zone</label>
                            <select className="md-input" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })}>
                                <option value="">Select Zone</option>
                                {(() => {
                                    let displayZones = zones;
                                    if (form.distance && !isNaN(parseFloat(form.distance))) {
                                        const dist = parseFloat(form.distance);
                                        const covering = zones.filter(z => z.distance >= dist);
                                        if (covering.length > 0) {
                                            const minCoverage = Math.min(...covering.map(z => z.distance));
                                            displayZones = covering.filter(z => z.distance === minCoverage);
                                        }
                                    }
                                    return displayZones.map(z => <option key={z._id} value={z._id}>{z.name} (Coverage: {z.distance} km)</option>);
                                })()}
                            </select>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Client</label>
                            <select className="md-input" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                                <option value="">Select Client</option>
                                {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                            </select>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Description</label>
                            <textarea className="md-textarea" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Add extra details here..." />
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Status</label>
                            <select className="md-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="map-wrapper" style={{ position: 'relative', minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                        <div ref={mapRef} style={{ flex: 1, background: '#f1f5f9', borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', zIndex: 1 }}>
                        </div>
                        <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b', border: '1px solid #e2e8f0' }}>
                            ℹ️ Locations selected via search will be visualized on the map instantly. Click on the map to manually add pickup, drop, or intermediate stops.
                        </div>
                    </div>
                </div>
                <div className="md-modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '16px 24px' }}>
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="md-save-btn"
                        onClick={() => onSave(form, stops)}
                        disabled={!form.name || !form.pickupLocation || !form.dropLocation}
                        style={{ opacity: (!form.name || !form.pickupLocation || !form.dropLocation) ? 0.7 : 1 }}
                    >
                        {data ? 'Update Route' : 'Create Route'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ImportWizard({ routes, operatorId, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    // Map of existing routes (lowercase name -> true)
    const existingNames = new Set(routes.map(r => (r.name || '').trim().toLowerCase()));

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                // Standardize headers
                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Route Name': 'udupi',
                'Pickup Location': 'Udupi, Karnataka, India',
                'Drop Location': 'Bramavara, Karnataka, India',
                'Distance (km)': 15.5,
                'Estimated Minutes': 25,
                'Client': 'Infosys',
                'Zone': 'Zone A',
                'Description': 'Regular staff transport route.',
                'Status': 'Active'
            },
            {
                'Route Name': 'It park',
                'Pickup Location': 'Whitefield, Bengaluru, Karnataka, India',
                'Drop Location': 'Rajajinagar, Bengaluru, Karnataka, India',
                'Distance (km)': 22.0,
                'Estimated Minutes': 45,
                'Client': 'Infosys',
                'Zone': 'Zone A',
                'Description': 'Night shift shuttle service.',
                'Status': 'Active'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [
            { wch: 18 }, { wch: 35 }, { wch: 35 },
            { wch: 14 }, { wch: 18 }, { wch: 15 },
            { wch: 12 }, { wch: 25 }, { wch: 10 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'Routes_Import_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const res = await axios.post(`${API}/routes/import`, {
                operatorId,
                routes: parsedData
            });
            setResult(res.data);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    const matchedCount = parsedData.filter(d => {
        const name = (d.name || d['Route Name'] || '').trim().toLowerCase();
        return name && existingNames.has(name);
    }).length;

    const newCount = parsedData.filter(d => {
        const name = (d.name || d['Route Name'] || '').trim().toLowerCase();
        const hasLocations = (d.pickupLocation || d['Pickup Location']) && (d.dropLocation || d['Drop Location']);
        return name && !existingNames.has(name) && hasLocations;
    }).length;

    const invalidCount = parsedData.length - matchedCount - newCount;

    if (result) {
        return (
            <div className="md-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The route data has been processed. Existing routes were updated and new routes were created.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', maxWidth: '700px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#065f46' }}>{result.updatedCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '4px' }}>Routes Updated</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>New Routes Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>

                    {result.errors && result.errors.length > 0 && (
                        <div style={{ maxWidth: '750px', margin: '0 auto 30px', textAlign: 'left' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Details / Warnings:</h4>
                            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                                {result.errors.map((err, idx) => (
                                    <div key={idx} style={{ fontSize: '13px', color: '#64748b', padding: '6px 0', borderBottom: idx < result.errors.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                        ⚠️ Row {err.row}: {err.message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onDone}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}
                    >
                        Return to Routes
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="md-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={onBack}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import & Update Routes</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new routes or update existing ones via Excel upload.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    {/* Instructions Left Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ℹ️ How it works
                            </h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>It matches uploaded rows to <strong>EXISTING</strong> routes in the database by their <strong>Route Name</strong> (case-insensitive) to update them.</li>
                                <li style={{ fontWeight: '600', color: '#16539C' }}>Unmatched route names will automatically be created as brand new routes!</li>
                                <li>Pickup & Drop location, Distance, Client, Zone, and Status will be mapped and set.</li>
                            </ul>
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Route Name *', 'Pickup Location', 'Drop Location', 'Distance (km)', 'Estimated Minutes', 'Client', 'Zone', 'Description', 'Status'].map((f, i) => (
                                    <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: '6px' }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <p style={{ fontSize: '11.5px', color: '#64748b', marginTop: '10px', margin: '10px 0 0' }}>* <strong>Route Name</strong> is required. Pickup and Drop locations are required when creating new routes.</p>
                        </div>

                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button
                                onClick={downloadTemplate}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>

                    {/* Upload Box Right Column */}
                    <div
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            background: dragActive ? '#f0f7ff' : 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px 40px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                            transition: 'all 0.15s',
                            cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input
                            id="file-upload-input"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                            Supports .xlsx, .xls formats
                        </span>
                    </div>
                </div>
            ) : (
                /* Preview Data Table & Actions */
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    {/* Summary & Buttons Bar */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{file?.name}</span>
                                <span style={{ fontSize: '12px', color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: '999px', fontWeight: '600' }}>
                                    {parsedData.length} Rows Found
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', marginTop: '6px' }}>
                                <span style={{ fontSize: '12.5px', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                                    🟢 {matchedCount} will update
                                </span>
                                <span style={{ fontSize: '12.5px', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                                    🔵 {newCount} will create
                                </span>
                                {invalidCount > 0 && (
                                    <span style={{ fontSize: '12.5px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                                        🔴 {invalidCount} invalid (missing locations)
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => { setFile(null); setParsedData([]); }}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                            >
                                Change File
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={importing || (matchedCount + newCount === 0)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '9px 22px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: (matchedCount + newCount > 0) ? 'linear-gradient(135deg,#16539C,#00B2A9)' : '#cbd5e1',
                                    color: 'white',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: (matchedCount + newCount > 0) ? 'pointer' : 'not-allowed',
                                    boxShadow: (matchedCount + newCount > 0) ? '0 4px 12px rgba(79,70,229,0.3)' : 'none',
                                    transition: 'all 0.15s'
                                }}
                            >
                                {importing ? 'Processing...' : `Proceed Import (${matchedCount + newCount})`}
                            </button>
                        </div>
                    </div>

                    {/* Preview Table */}
                    <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Import Status</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Route Name</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Pickup Location</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Drop Location</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Distance</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Est. Minutes</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Client</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((d, i) => {
                                    const rName = (d.name || d['Route Name'] || '').trim();
                                    const isMatch = existingNames.has(rName.toLowerCase());
                                    const hasLocations = (d.pickupLocation || d['Pickup Location']) && (d.dropLocation || d['Drop Location']);

                                    let statusText = '';
                                    let statusStyle = {};
                                    let rowBg = '#fff';

                                    if (isMatch) {
                                        statusText = 'Updates Existing';
                                        statusStyle = { background: '#d1fae5', color: '#065f46' };
                                    } else if (hasLocations) {
                                        statusText = 'Creates New';
                                        statusStyle = { background: '#dbeafe', color: '#1e40af' };
                                        rowBg = '#f0f7ff';
                                    } else {
                                        statusText = 'Invalid (Missing Locations)';
                                        statusStyle = { background: '#fee2e2', color: '#991b1b' };
                                        rowBg = '#fef2f2';
                                    }

                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: rowBg }}>
                                            <td style={{ padding: '14px 20px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: '700', padding: '4px 8px', borderRadius: '6px', ...statusStyle }}>
                                                    {statusText}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 20px', fontWeight: '700', color: '#1e293b' }}>
                                                {rName || '—'}
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>
                                                {d.pickupLocation || d['Pickup Location'] || '—'}
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>
                                                {d.dropLocation || d['Drop Location'] || '—'}
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>
                                                {d.distance || d['Distance (km)'] || d['Distance'] || '—'}
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>
                                                {d.estimatedMinutes || d['Estimated Minutes'] || d['Est. Minutes'] || '—'}
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>
                                                {d.client || d['Client'] || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
